import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageHero from "../components/PageHero";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Calendar, User, Search, ArrowRight, ArrowLeft } from "lucide-react";

export function NewsList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (search = q, category = cat) => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "100" });
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    const { data } = await api.get(`/news?${params.toString()}`);
    setItems(data.items);
    setLoading(false);
  };
  useEffect(() => { load("", ""); }, []);

  const categories = Array.from(new Set(items.map((i) => i.category)));
  const featured = items.find((i) => i.featured) || items[0];
  const rest = items.filter((i) => i.id !== featured?.id);

  return (
    <div data-testid="news-list-page">
      <PageHero eyebrow="Berita & Informasi" title="Berita Sekolah" subtitle="Terus terhubung dengan kabar terbaru, prestasi, kegiatan, dan pengumuman dari SMAS YPK 2 Biak." />
      <section className="py-10 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap gap-4 items-center justify-between">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Input data-testid="news-search" placeholder="Cari berita..." value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(q, cat)} className="pl-10 h-11 rounded-full" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { setCat(""); load(q, ""); }} className={`px-3.5 py-1.5 rounded-full text-sm font-semibold ${!cat ? "bg-[#0B1D3A] text-white" : "bg-slate-100 text-slate-700"}`}>Semua</button>
            {categories.map((c) => (
              <button key={c} onClick={() => { setCat(c); load(q, c); }} data-testid={`news-cat-${c.toLowerCase()}`}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold ${cat === c ? "bg-[#0B1D3A] text-white" : "bg-slate-100 text-slate-700"}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading && <div className="text-slate-500">Memuat...</div>}
          {!loading && featured && (
            <Link to={`/berita/${featured.id}`} data-testid={`featured-news-${featured.id}`} className="card-elevated overflow-hidden grid lg:grid-cols-2 group mb-10">
              <div className="aspect-video lg:aspect-auto overflow-hidden">
                <img src={featured.thumbnail_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <Badge className="bg-amber-500 text-[#0B1D3A] self-start uppercase text-xs tracking-wider font-bold">Featured</Badge>
                <h2 className="font-serif text-3xl font-bold text-[#0B1D3A] mt-4 leading-tight group-hover:text-amber-600 transition-colors">{featured.title}</h2>
                <p className="mt-4 text-slate-600 line-clamp-3">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(featured.created_at).toLocaleDateString("id-ID")}</span>
                  <span className="flex items-center gap-1"><User className="w-3 h-3" /> {featured.author}</span>
                </div>
              </div>
            </Link>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((n) => (
              <Link key={n.id} to={`/berita/${n.id}`} data-testid={`news-${n.id}`} className="card-elevated overflow-hidden group">
                <div className="aspect-video overflow-hidden">
                  <img src={n.thumbnail_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[11px] uppercase tracking-wider font-bold">{n.category}</Badge>
                  <div className="font-serif text-lg font-bold text-[#0B1D3A] mt-3 leading-snug group-hover:text-amber-600 transition-colors">{n.title}</div>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2">{n.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                    <span>{new Date(n.created_at).toLocaleDateString("id-ID")}</span>
                    <span className="text-amber-600 font-semibold inline-flex items-center gap-1">Baca <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function NewsDetail() {
  const { id } = useParams();
  const [n, setN] = useState(null);
  useEffect(() => { api.get(`/news/${id}`).then(({ data }) => setN(data)).catch(() => {}); }, [id]);
  if (!n) return <div className="pt-40 text-center text-slate-500">Memuat...</div>;
  return (
    <div data-testid="news-detail-page">
      <section className="relative pt-32 pb-16 bg-[#0B1D3A] text-white">
        <div className="absolute inset-0">
          <img src={n.thumbnail_url} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1D3A]/80 to-[#0B1D3A]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <Link to="/berita" className="inline-flex items-center gap-2 text-amber-300 text-sm font-semibold mb-6"><ArrowLeft className="w-4 h-4" /> Semua Berita</Link>
          <Badge className="bg-amber-500 text-[#0B1D3A] uppercase text-xs tracking-wider font-bold">{n.category}</Badge>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black mt-4 leading-tight">{n.title}</h1>
          <div className="mt-6 flex items-center gap-5 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(n.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {n.author}</span>
          </div>
        </div>
      </section>
      <section className="py-16 lg:py-20 bg-white">
        <article className="max-w-3xl mx-auto px-6 lg:px-8 prose-article">
          <p className="text-xl text-slate-600 leading-relaxed mb-8 font-light">{n.excerpt}</p>
          <div className="news-content" dangerouslySetInnerHTML={{ __html: n.content }} />
          {n.source_url && (
            <div className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500">
              Sumber asli: <a href={n.source_url} target="_blank" rel="noreferrer" className="text-amber-600 hover:underline">kopipahit91.blogspot.com</a>
            </div>
          )}
        </article>
      </section>
    </div>
  );
}
